import { defineStore } from 'pinia';
import { v4 } from 'uuid';
import {
  IdleConnectionStrategy,
  SSHMessage,
  WebsocketSSHConnection
} from './components/robotConnection/robotConnection';
import { V4UUID, workspaceStateAsync } from '@/firebase/database';
import {
  addLaunchFiles,
  getAllWorkspaceConnections,
  getWorkspaceLaunchFiles,
  saveLaunchFiles,
  saveWorkspaceConnection
} from '@/firebase/databaseDependencyInjectables';
import { LaunchFile } from './components/robotConnection/loadLaunchFilesStrategy';
import { RunRobotCommandStrategy } from './components/robotConnection/runRobotCommandStrategy';
import { removeElement } from '../globalModule/components/helperMethods';
export const useControlCenterStore = defineStore('controlCenter', {
  state: (): ControlCenterState => {
    return {
      workspaceName: 'Project',
      workspaceId: null,
      currConnection: null,
      previousConnections: [],
      launchFiles: []
    };
  },
  actions: {
    async setWorkspaceId(id: V4UUID) {
      this.workspaceId = id;
      const wsState = await workspaceStateAsync(id);
      this.workspaceName = wsState ? wsState.name : this.workspaceName;
    },
    setLaunchFiles(launchFiles: LaunchFile[]) {
      this.launchFiles = launchFiles;
    },
    addLaunchFiles(launchFiles: LaunchFile[]) {
      this.launchFiles = [...new Set(this.launchFiles.concat(launchFiles))];
    },
    loadLaunchFiles() {
      if (!this.workspaceId) return;
      this.launchFiles = getWorkspaceLaunchFiles(this.workspaceId);
      console.log(this.launchFiles);
    },
    saveLaunchFiles(addInstead = false) {
      if (!this.workspaceId) return;
      if (addInstead) {
        addLaunchFiles(this.workspaceId, this.launchFiles);
      } else {
        saveLaunchFiles(this.workspaceId, this.launchFiles);
      }
    },
    /**
     * loads connections and sorts them so that most recently used connection is first
     * @returns
     */
    loadConnections(): void {
      if (!this.workspaceId || !getAllWorkspaceConnections(this.workspaceId))
        return;
      this.previousConnections = Object.entries(
        getAllWorkspaceConnections(this.workspaceId)
      )
        .map((obj) => {
          return ConnectionDetails.fromJSON(obj[1]);
        })
        .sort(
          (connA: ConnectionDetails, connB: ConnectionDetails) =>
            connB.lastUsed - connA.lastUsed
        );
    },
    saveCurrentDetails(): void {
      if (!this.workspaceId || !this.currConnection) return;
      saveWorkspaceConnection(
        this.workspaceId,
        this.currConnection.connectionDetails
      );
    },

    // connects to an SSh endpoint. stores connection details in store, and database
    storeConnection(
      hostname: string,
      username: string,
      port: number,
      domainId: number,
      sourcePath: string,
      password: string,
      setStatus: (status: string) => void = () => {}
    ): void {
      // for connection persistence
      let connectionDetails: ConnectionDetails | undefined =
        this.previousConnections.find(
          (val: ConnectionDetails) =>
            val.hostname === hostname &&
            val.username === username &&
            val.port === port &&
            val.domainId === domainId
        );
      if (!connectionDetails) {
        // create a new connection if not match
        connectionDetails = new ConnectionDetails(
          hostname,
          username,
          port,
          domainId,
          sourcePath
        );
        this.previousConnections.push(connectionDetails);
      }
      connectionDetails.lastUsed = Date.now();
      connectionDetails.sourcePath = sourcePath;
      const connection: WebsocketSSHConnection = new WebsocketSSHConnection(
        connectionDetails,
        password
      );
      connection.updateStatus = (status: string) => {
        setStatus(status);
        if (
          status != 'connected' &&
          this.currConnection &&
          !status.includes('Connection failed')
        ) {
          this.disconnect();
        }
      };
      this.currConnection = connection;
      this.saveCurrentDetails();
    },
    disconnect(): void {
      if (!this.currConnection) return;
      this.currConnection.disconnect();
      this.currConnection = null;
    },
    runLaunchFile(launchFile: LaunchFile): void {
      // check if not running other process
      if (
        !this.currConnection ||
        (!(
          this.currConnection?.connectionStrategy instanceof
          IdleConnectionStrategy
        ) &&
          !(
            this.currConnection?.connectionStrategy instanceof
            RunRobotCommandStrategy
          ))
      ) {
        console.log('skipping launch file');
        return;
      }
      // switch to run launch file strategy
      if (
        !(
          this.currConnection?.connectionStrategy instanceof
          RunRobotCommandStrategy
        )
      ) {
        this.currConnection?.setStrategy(
          new RunRobotCommandStrategy(
            this.currConnection.connectionDetails.getSourceCommand()
          )
        );
      }
      // send tmux command to run launch file
      this.currConnection?.sendMessage(
        this.getLaunchStrategy()!.getStartLaunchFileMsg(launchFile).toMessage()
      );
      // track running launch file
      this.getLaunchStrategy()!.runningLaunchFiles.push(launchFile);
    },
    stopLaunchFile(launchFile: LaunchFile): void {
      // check if not running other process
      if (!this.getLaunchStrategy() || !this.isLaunchFileRunning(launchFile)) {
        return;
      }
      // send command to stop launch file
      this.currConnection?.sendMessage(
        this.getLaunchStrategy()!.getStopLaunchFileMsg(launchFile).toMessage()
      );
      // remove from launch file list
      console.log(this.getLaunchStrategy()!.runningLaunchFiles);
      console.log(launchFile);
      this.getLaunchStrategy()!.runningLaunchFiles =
        this.getLaunchStrategy()!.runningLaunchFiles.filter(
          (testLaunch: LaunchFile) =>
            testLaunch.name !== launchFile.name ||
            testLaunch.pkgName !== launchFile.pkgName ||
            testLaunch.path !== launchFile.path
        );
    },
    isLaunchFileRunning(launchFile: LaunchFile): boolean {
      if (!this.getLaunchStrategy()) {
        return false;
      }
      return (
        this.currConnection?.connectionStrategy as RunRobotCommandStrategy
      ).runningLaunchFiles.some(
        (testLaunch: LaunchFile) =>
          testLaunch.name === launchFile.name &&
          testLaunch.pkgName === launchFile.pkgName &&
          testLaunch.path === launchFile.path
      );
    },
    runRosVnc(command: string, windowName: string): void {
      // check if not running other process
      if (
        !this.currConnection ||
        (!(
          this.currConnection?.connectionStrategy instanceof
          IdleConnectionStrategy
        ) &&
          !(
            this.currConnection?.connectionStrategy instanceof
            RunRobotCommandStrategy
          ))
      ) {
        return;
      }
      // switch to run launch file strategy
      if (
        !(
          this.currConnection?.connectionStrategy instanceof
          RunRobotCommandStrategy
        )
      ) {
        this.currConnection?.setStrategy(
          new RunRobotCommandStrategy(
            this.currConnection.connectionDetails.getSourceCommand()
          )
        );
      }

      this.currConnection?.sendMessage(
        this.getLaunchStrategy()!
          .getStartRosMsg(command, windowName)
          .toMessage()
      );
    },
    /**
     * returns null if no connection or if the connection strategy is not run launch file strategy
     * else returns the run launch file connection strategy
     * @returns
     */
    getLaunchStrategy(): RunRobotCommandStrategy | null {
      if (
        !this.currConnection ||
        !(
          this.currConnection.connectionStrategy instanceof
          RunRobotCommandStrategy
        )
      )
        return null;
      return this.currConnection.connectionStrategy as RunRobotCommandStrategy;
    }
  }
});

export interface ControlCenterState {
  workspaceName: string;
  workspaceId: null | V4UUID;
  currConnection: null | WebsocketSSHConnection;
  previousConnections: ConnectionDetails[];
  launchFiles: LaunchFile[];
}

export class ConnectionDetails {
  id: V4UUID;
  hostname: string;
  port: number;
  username: string;
  packagesLoaded: boolean;
  sourcePath: string;
  extraCommands: string;
  lastUsed: number;
  buildDir: string;
  domainId: number;
  constructor(
    hostname: string,
    username: string,
    port: number = 22,
    domainId: number = 30,
    sourcePath: string = '',
    id: V4UUID = v4(),
    extraCommands: string = '',
    packagesLoaded: boolean = false,
    buildDir: string = '',
    lastUsed = Date.now()
  ) {
    this.hostname = hostname;
    this.username = username;
    this.port = port;
    this.domainId = domainId;
    this.id = id;
    this.sourcePath = sourcePath;
    this.extraCommands = extraCommands;
    this.packagesLoaded = packagesLoaded;
    this.buildDir = buildDir;
    this.lastUsed = lastUsed;
  }
  getSourceCommand(): string {
    let sourceString = `export ROS_DOMAIN_ID=${this.domainId} > /dev/null ; `;
    if (this.sourcePath.trim() !== '')
      sourceString += ` source ${this.sourcePath} > /dev/null ; `;
    if (this.extraCommands.trim() !== '')
      this.extraCommands
        .trim()
        .split('\n')
        .forEach((cmd) => {
          sourceString += `${cmd} > /dev/null ; `;
        });
    return sourceString;
  }

  static fromJSON(jsonDetails: any) {
    return new ConnectionDetails(
      jsonDetails.hostname,
      jsonDetails.username,
      jsonDetails.port,
      jsonDetails.domainId,
      jsonDetails.sourcePath,
      jsonDetails.id,
      jsonDetails.extraCommands,
      jsonDetails.packagesLoaded,
      jsonDetails.buildDir || '',
      jsonDetails.lastUsed
    );
  }
}
